import React, { useEffect, useRef } from 'react';
import {
  createChart, CandlestickSeries, HistogramSeries, LineSeries, AreaSeries,
  type IChartApi, type ISeriesApi, type UTCTimestamp, type Time,
} from 'lightweight-charts';
import { colors } from '../styles/fdsTheme';

/**
 * TradingView lightweight-charts 래퍼 (2026-11-03).
 * 트레이딩(샘플)·투자 대시보드가 같이 쓴다.
 *
 *   mode='candle' : 캔들 + 거래량 + MA5/20/60
 *   mode='area'   : 종가 영역선 (자산 추이 같은 단일 값)
 *
 * 한국 관례대로 상승 빨강 · 하락 파랑.
 */

export interface Bar { time: Time; open: number; high: number; low: number; close: number; volume: number; }

export const MA_DEFS = [
  { period: 5, color: '#ef4444' },
  { period: 20, color: '#22c55e' },
  { period: 60, color: '#3b82f6' },
];

/** KST 시각을 UTC 숫자로 그대로 둔다 — 차트 축에 09:00 이 09:00 으로 찍히게 */
export const kstEpoch = (d: string, t: string): UTCTimestamp => {
  const ds = d.replace(/-/g, '');
  return (Date.UTC(+ds.slice(0, 4), +ds.slice(4, 6) - 1, +ds.slice(6, 8), +t.slice(0, 2), +t.slice(2, 4)) / 1000) as UTCTimestamp;
};

const sma = (bars: Bar[], n: number) => {
  const out: { time: Time; value: number }[] = [];
  let sum = 0;
  bars.forEach((b, i) => {
    sum += b.close;
    if (i >= n) sum -= bars[i - n].close;
    if (i >= n - 1) out.push({ time: b.time, value: sum / n });
  });
  return out;
};

export interface ChartTheme { bg: string; text: string; grid: string; border: string; }
export const lightChart: ChartTheme = { bg: colors.cardBg, text: colors.body, grid: colors.dividerLight, border: colors.divider };

interface Props {
  bars: Bar[];
  height: number;
  intraday?: boolean;
  mode?: 'candle' | 'area';
  theme?: ChartTheme;
  /** area 모드 선 색 */
  lineColor?: string;
}

const TvChart: React.FC<Props> = ({ bars, height, intraday = false, mode = 'candle', theme = lightChart, lineColor = colors.btnPrimary }) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<{
    candle?: ISeriesApi<'Candlestick'>; vol?: ISeriesApi<'Histogram'>; ma: ISeriesApi<'Line'>[]; area?: ISeriesApi<'Area'>;
  } | null>(null);

  useEffect(() => {
    if (!boxRef.current) return;
    const chart = createChart(boxRef.current, {
      height,
      autoSize: true,
      layout: { background: { color: theme.bg }, textColor: theme.text, fontSize: 11 },
      grid: { vertLines: { color: theme.grid }, horzLines: { color: theme.grid } },
      rightPriceScale: { borderColor: theme.border },
      timeScale: { borderColor: theme.border, timeVisible: intraday, secondsVisible: false },
      crosshair: { mode: 0 },
      localization: { priceFormatter: (p: number) => Math.round(p).toLocaleString() },
    });
    if (mode === 'area') {
      const area = chart.addSeries(AreaSeries, {
        lineColor, lineWidth: 2, topColor: `${lineColor}55`, bottomColor: `${lineColor}05`, priceLineVisible: false,
      });
      seriesRef.current = { ma: [], area };
    } else {
      const candle = chart.addSeries(CandlestickSeries, {
        upColor: colors.up, downColor: colors.down, borderVisible: false,
        wickUpColor: colors.up, wickDownColor: colors.down,
      });
      const vol = chart.addSeries(HistogramSeries, { priceScaleId: 'vol', priceFormat: { type: 'volume' } });
      chart.priceScale('vol').applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
      candle.priceScale().applyOptions({ scaleMargins: { top: 0.05, bottom: 0.25 } });
      const ma = MA_DEFS.map(m => chart.addSeries(LineSeries, {
        color: m.color, lineWidth: 1, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
      }));
      seriesRef.current = { candle, vol, ma };
    }
    chartRef.current = chart;
    return () => { chart.remove(); chartRef.current = null; seriesRef.current = null; };
  }, [height, mode, theme, lineColor]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const s = seriesRef.current;
    if (!s) return;
    if (s.area) {
      s.area.setData(bars.map(b => ({ time: b.time, value: b.close })));
    } else {
      s.candle!.setData(bars.map(({ time, open, high, low, close }) => ({ time, open, high, low, close })));
      s.vol!.setData(bars.map(b => ({
        time: b.time, value: b.volume,
        color: b.close >= b.open ? 'rgba(220,38,38,0.35)' : 'rgba(37,99,235,0.35)',
      })));
      MA_DEFS.forEach((m, i) => s.ma[i].setData(sma(bars, m.period)));
    }
    chartRef.current?.timeScale().applyOptions({ timeVisible: intraday });
    chartRef.current?.timeScale().fitContent();
  }, [bars, intraday, mode, theme, lineColor, height]);

  return <div ref={boxRef} style={{ width: '100%', height }} />;
};

export default TvChart;
